/**
 * Build normalized invoice timeline entries (job-timeline shape) from invoice
 * record + revision history + documents + notifications.
 *
 * Entry shape: { id, title, description, type, createdAt, createdBy, documentLinks? }
 *
 * "Revised" rows come only from revision-history API rows (invoice_revisions).
 * Status=Revised alone does not invent a Revised event — callers should show
 * an honest gap message when status is Revised but revisions[] is empty.
 */

import { revisionDocumentLinks } from '@/utils/invoice-file-loader'

function pickTimestamp(...candidates) {
  for (const value of candidates) {
    if (value) return value
  }
  return null
}

function formatAmount(value) {
  const num = parseFloat(value)
  if (Number.isNaN(num)) return null
  return num.toLocaleString('en-IN', { maximumFractionDigits: 2 })
}

function revisionDescription(rev) {
  const parts = []
  const changeType = rev.amount_change_type || rev.change_type
  if (rev.revision_type === 'basic') {
    parts.push('Basic info change')
    if (rev.amend_invoice === 'yes') parts.push('R1 amendment required')
  } else if (rev.revision_type === 'amount') {
    const note = changeType === 'increase' || changeType === 'debit'
      ? 'Debit note'
      : (changeType === 'decrease' || changeType === 'credit' ? 'Credit note' : 'Amount change')
    parts.push(changeType === 'increase' || changeType === 'decrease' || changeType === 'debit' || changeType === 'credit'
      ? `Amount change (${note})`
      : 'Amount change')
  } else if (changeType) {
    parts.push(`Change: ${changeType}`)
  }
  if (rev.note_type) parts.push(`Note: ${rev.note_type}`)
  if (rev.revision_reason) parts.push(rev.revision_reason)
  if (rev.comment) parts.push(rev.comment)
  const oldAmt = formatAmount(rev.old_amount)
  const newAmt = formatAmount(rev.new_amount != null ? rev.new_amount : rev.total_amount)
  if (oldAmt && newAmt) parts.push(`₹${oldAmt} → ₹${newAmt}`)
  else if (newAmt) parts.push(`Total: ₹${newAmt}`)
  if (rev.status) parts.push(`Status: ${rev.status}`)
  return parts.filter(Boolean).join(' · ') || 'Invoice revised'
}

function normalizeRevisionList(revisions) {
  if (!revisions) return []
  if (Array.isArray(revisions)) return revisions
  if (Array.isArray(revisions.revisions)) return revisions.revisions
  if (Array.isArray(revisions.history)) return revisions.history
  if (Array.isArray(revisions.data)) return revisions.data
  return []
}

function normalizeList(payload) {
  if (!payload) return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload.data)) return payload.data
  if (payload.data && Array.isArray(payload.data.items)) return payload.data.items
  return []
}

export function isRevisedStatus(status) {
  return String(status || '').trim().toLowerCase() === 'revised'
}

/**
 * True when the timeline already has a real Revised event from revision history.
 */
export function timelineHasRevisedEvent(entries) {
  if (!Array.isArray(entries)) return false
  return entries.some((e) => {
    const title = String((e && e.title) || '')
    const id = String((e && e.id) || '')
    return /—\s*Revised/i.test(title) || id.startsWith('revision-')
  })
}

export function buildInvoiceTimeline({
  invoice = null,
  revisions = [],
  signedInvoices = [],
  documents = [],
  notifications = [],
} = {}) {
  const entries = []
  const inv = invoice || {}
  const invId = inv.invoice_id || inv.invoice_number || inv.id || ''
  const revisionRows = normalizeRevisionList(revisions)

  const createdAt = pickTimestamp(inv.created_at, inv.createdAt, inv.invoice_date)
  if (createdAt) {
    entries.push({
      id: `created-${invId}`,
      title: `${invId || 'Invoice'} — Created`,
      description: [
        inv.category ? `Category: ${inv.category}` : null,
        inv.client || inv.client_name ? `Client: ${inv.client || inv.client_name}` : null,
        inv.status ? `Status: ${inv.status}` : null,
      ].filter(Boolean).join(' · ') || 'Invoice record created',
      type: 'success',
      createdAt,
      createdBy: inv.added_by || inv.created_by || '',
    })
  }

  if (inv.email_date) {
    entries.push({
      id: `email-${invId}-${inv.email_date}`,
      title: `${invId || 'Invoice'} — Emailed to Client`,
      description: `Invoice email date: ${inv.email_date}`,
      type: 'primary',
      createdAt: inv.email_date,
      createdBy: inv.updated_by || inv.added_by || '',
    })
  }

  revisionRows.forEach((rev, idx) => {
    const revNum = rev.revision_number != null && rev.revision_number !== ''
      ? rev.revision_number
      : (rev.id != null ? rev.id : idx + 1)
    const docLinks = revisionDocumentLinks(rev)
    // Prefer real timestamps; fall back so history rows are never silently dropped
    // when backend omits created_at (UI shows "—" for missing times).
    const revAt = pickTimestamp(
      rev.created_at,
      rev.createdAt,
      rev.updated_at,
      rev.updatedAt,
      inv.updated_at,
      inv.updatedAt,
    ) || '—'
    const actor = rev.created_by
      || rev.initiated_by
      || rev.approved_by
      || rev.updated_by
      || ''
    entries.push({
      id: `revision-${rev.id || revNum}`,
      title: `${invId || 'Invoice'} — Revised${revNum != null && revNum !== '' ? ` (#${revNum})` : ''}`,
      description: revisionDescription(rev),
      type: 'warning',
      createdAt: revAt,
      createdBy: actor,
      documentLinks: docLinks.length ? docLinks : undefined,
    })

    const statusAfter = rev.status_after || rev.status
    if (rev.status_before && statusAfter && rev.status_before !== statusAfter) {
      entries.push({
        id: `status-rev-${rev.id || revNum}`,
        title: `${invId || 'Invoice'} — Status Updated to ${statusAfter}`,
        description: `Previous status: ${rev.status_before}`,
        type: 'info',
        createdAt: revAt,
        createdBy: actor,
      })
    }
  })

  const paidAmount = inv.paid_amount
  const hasPaid = paidAmount !== null && paidAmount !== undefined && paidAmount !== '' && parseFloat(paidAmount) > 0
  if (hasPaid && inv.paid_date) {
    entries.push({
      id: `payment-${invId}-${inv.paid_date}`,
      title: `${invId || 'Invoice'} — Payment Recorded`,
      description: [
        `Amount: ₹${formatAmount(paidAmount)}`,
        inv.status ? `Status: ${inv.status}` : null,
        inv.payment_comment ? inv.payment_comment : null,
      ].filter(Boolean).join(' · '),
      type: 'success',
      createdAt: pickTimestamp(inv.paid_date, inv.payment_date, inv.updated_at),
      createdBy: inv.updated_by || inv.added_by || '',
    })
  } else if (inv.payment_date && inv.payment_amount) {
    entries.push({
      id: `payment-partial-${invId}-${inv.payment_date}`,
      title: `${invId || 'Invoice'} — Partial Payment`,
      description: `Amount: ₹${formatAmount(inv.payment_amount)}`,
      type: 'success',
      createdAt: inv.payment_date,
      createdBy: inv.updated_by || inv.added_by || '',
    })
  }

  if (String(inv.status || '').toLowerCase() === 'cancelled') {
    entries.push({
      id: `cancelled-${invId}`,
      title: `${invId || 'Invoice'} — Cancelled`,
      description: inv.cancellation_reason || inv.comment || 'Invoice cancelled',
      type: 'danger',
      createdAt: pickTimestamp(inv.updated_at, inv.cancelled_at),
      createdBy: inv.updated_by || inv.added_by || '',
    })
  }

  signedInvoices.forEach((doc, idx) => {
    entries.push({
      id: `signed-${doc.id || idx}`,
      title: `${invId || 'Invoice'} — Signed Invoice Uploaded`,
      description: doc.file_name || doc.filename || doc.original_name || doc.name || 'Signed PDF',
      type: 'info',
      createdAt: pickTimestamp(doc.uploaded_at, doc.created_at, doc.createdAt),
      createdBy: doc.uploaded_by || doc.uploadedBy || doc.created_by || '',
    })
  })

  documents.forEach((doc, idx) => {
    const docType = String(doc.documentType || doc.document_type || doc.type || 'document').toLowerCase()
    if (docType.includes('signed')) return
    entries.push({
      id: `doc-${doc.id || idx}`,
      title: `${invId || 'Invoice'} — Document Uploaded`,
      description: [
        docType !== 'document' ? docType.replace(/_/g, ' ') : null,
        doc.file_name || doc.filename || doc.name || doc.document_name,
      ].filter(Boolean).join(' · ') || 'Document uploaded',
      type: 'info',
      createdAt: pickTimestamp(doc.uploaded_at, doc.created_at, doc.createdAt),
      createdBy: doc.uploaded_by || doc.created_by || '',
    })
  })

  normalizeList(notifications).forEach((note, idx) => {
    entries.push({
      id: `notif-${note.id || idx}`,
      title: `${invId || 'Invoice'} — Notification${note.notification_type || note.type ? `: ${note.notification_type || note.type}` : ''}`,
      description: note.message || note.subject || note.status || 'Notification sent',
      type: 'primary',
      createdAt: pickTimestamp(note.sent_at, note.created_at, note.scheduled_at),
      createdBy: note.sent_by || note.created_by || '',
    })
  })

  const updatedAt = inv.updated_at
  if (updatedAt && createdAt && updatedAt !== createdAt && !revisionRows.length) {
    entries.push({
      id: `updated-${invId}-${updatedAt}`,
      title: `${invId || 'Invoice'} — Updated`,
      description: inv.status ? `Current status: ${inv.status}` : 'Invoice details updated',
      type: 'info',
      createdAt: updatedAt,
      createdBy: inv.updated_by || inv.added_by || '',
    })
  }

  const sorted = entries
    .filter((e) => e.createdAt)
    .sort((a, b) => {
      if (a.createdAt === '—' && b.createdAt === '—') return 0
      if (a.createdAt === '—') return 1
      if (b.createdAt === '—') return -1
      return new Date(b.createdAt) - new Date(a.createdAt)
    })
    .slice(0, 50)

  return sorted
}

/**
 * Meta for honest UX when status is Revised but revision history is empty.
 */
export function buildInvoiceTimelineMeta({ invoice = null, revisions = [] } = {}) {
  const rows = normalizeRevisionList(revisions)
  const statusRevised = isRevisedStatus(invoice && invoice.status)
  return {
    revisionCount: rows.length,
    statusIsRevised: statusRevised,
    revisionHistoryGap: statusRevised && rows.length === 0,
  }
}

export default buildInvoiceTimeline
